import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { reaction } from 'mobx';
import { Text } from 'preact-i18n';

import renderArticleBody from '../articleRender/bodyRenderer';
import { diffWords, countChanges } from '../../lib/textDiff';
import { isRewriteAction } from '../../lib/articleAssist';

const PREVIEW_DELAY_MS = 300;

/**
 * Live preview of the article body while editing. Reads the body from the
 * article store (input.js updates it on every keystroke) and re-renders it
 * through the same renderer as the public article view, debounced so typing
 * stays smooth. Maps and videos are shown as placeholders.
 */
@observer
class ArticleBodyPreview extends Component {
    constructor(props) {
        super(props);
        const { articleStore } = props.stores;
        const article = articleStore[props.objectName] || {};
        this.state = { body: article.body || '' };
    }

    componentDidMount() {
        const { articleStore } = this.props.stores;
        const { objectName } = this.props;
        this.disposer = reaction(
            () => articleStore[objectName]?.body,
            body => this.setState({ body: body || '' }),
            { delay: PREVIEW_DELAY_MS },
        );
    }

    componentWillUnmount() {
        if (this.disposer) this.disposer();
    }

    renderMapPlaceholder = (article) => {
        const count = article.mapMarkers?.length || 0;
        return (
            <div key='map-placeholder' class='border rounded-3 p-3 my-2 text-center text-body-secondary'>
                <i class='fa-solid fa-map me-2' />
                <Text id='article.previewMapPlaceholder' fields={{ count }}>Map ({count} markers)</Text>
            </div>
        );
    }

    renderVideoPlaceholder = (file) => {
        if (file.s3LinkStreamPreview1) {
            return <img src={file.s3LinkStreamPreview1} class='w-100 rounded-3' alt='' />;
        }
        return (
            <div class='border rounded-3 p-3 text-center text-body-secondary'>
                <i class='fa-solid fa-video me-2' />
                <Text id='article.previewVideoPlaceholder'>Video</Text>
            </div>
        );
    }

    applyDiff = (e) => {
        const { how } = e.currentTarget.dataset;
        const { onApply } = this.props;
        if (onApply) onApply(how);
    }

    // Change view: the AI suggestion against the current body, word by word.
    renderDiff(article, result) {
        const segments = diffWords(article.body || '', result.text || '');
        const changes = countChanges(segments);
        return (
            <div class='article-body-diff'>
                <div class='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                    <span class='text-body-secondary small'>
                        <i class='fa-solid fa-message-bot me-1' />
                        <Text id='article.aiDiffHeader' fields={{ count: changes }}>AI suggestion: {changes} changes</Text>
                    </span>
                    <span class='d-flex gap-1'>
                        <button type='button' class='btn btn-sm btn-primary rounded-pill' data-how='replace' onClick={this.applyDiff}>
                            <i class='fa-solid fa-check me-1' />
                            <Text id='article.aiAcceptChanges'>Accept changes</Text>
                        </button>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' data-how='discard' onClick={this.applyDiff}>
                            <i class='fa-solid fa-xmark me-1' />
                            <Text id='article.aiDiscard'>Discard</Text>
                        </button>
                    </span>
                </div>
                <div class='ai-diff-text'>
                    {segments.map((seg, idx) => {
                        if (seg.type === 'del') return <del key={`d-${idx}`} class='ai-diff-del'>{seg.text}</del>;
                        if (seg.type === 'ins') return <ins key={`i-${idx}`} class='ai-diff-ins'>{seg.text}</ins>;
                        return <span key={`e-${idx}`}>{seg.text}</span>;
                    })}
                </div>
            </div>
        );
    }

    render() {
        const { stores, objectName } = this.props;
        const { articleStore, userStore } = stores;
        const { body } = this.state;
        const article = articleStore[objectName] || {};
        const { language } = userStore;
        const { action, result } = articleStore.aiAssist || {};

        if (result && isRewriteAction(action) && typeof result.text === 'string') {
            return this.renderDiff(article, result);
        }

        return (
            <div class='article-body-preview'>
                <div class='text-body-secondary small mb-2'>
                    <i class='fa-solid fa-eye me-1' />
                    <Text id='article.previewLive'>This is how readers will see the text</Text>
                </div>
                {body?.trim() ? (
                    <div class='d-flex flex-column'>
                        {renderArticleBody({
                            article,
                            body,
                            language,
                            stores,
                            renderVideoFile: this.renderVideoPlaceholder,
                            renderArticleMap: this.renderMapPlaceholder,
                        })}
                    </div>
                ) : (
                    <div class='text-body-tertiary fst-italic py-4 text-center'>
                        <Text id='article.previewEmpty'>Start typing on the left and the text shows up here.</Text>
                    </div>
                )}
            </div>
        );
    }
}

export default ArticleBodyPreview;
